import type { SerializedActionPayload } from '@/lib/actions/serialization';
import type { ProfileResponse } from '@/lib/validation/api';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';
import {
  decodePayload,
  type UserPreviewCacheDetailLevel,
  type UserPreviewCacheRecord,
} from '../user-preview-cache.types';
import {
  buildProfileCacheKey,
  buildUsernameCacheKey,
  extractProfileIdFromKey,
  extractUsernameFromKey,
  isProfileCacheKey,
  isUsernameCacheKey,
  mergeAliases,
  normalizeUsernameKey,
} from './user-preview-cache.keys';
import { hasMeaningfulPreview } from './user-preview-cache.utils';

export type UserPreviewCacheRecordDraft = Partial<Omit<UserPreviewCacheRecord, 'preview' | 'profile'>> & {
  preview?: UserPreviewVM | SerializedActionPayload | null;
  profile?: ProfileResponse | SerializedActionPayload | null;
  username?: string;
};

const coercePreview = (
  draft: UserPreviewCacheRecordDraft,
  previous: UserPreviewCacheRecord | undefined
): UserPreviewVM | undefined => {
  if (Object.hasOwn(draft, 'preview')) {
    if (draft.preview === null) {
      return;
    }
    return decodePayload<UserPreviewVM>(draft.preview);
  }
  return previous?.preview;
};

const coerceProfile = (
  draft: UserPreviewCacheRecordDraft,
  previous: UserPreviewCacheRecord | undefined
): ProfileResponse | undefined => {
  if (Object.hasOwn(draft, 'profile')) {
    if (draft.profile === null) {
      return;
    }
    return decodePayload<ProfileResponse>(draft.profile);
  }
  return previous?.profile;
};

const coerceError = (draft: UserPreviewCacheRecordDraft, previous: UserPreviewCacheRecord | undefined): boolean => {
  if ('error' in draft) {
    return Boolean(draft.error);
  }
  return previous?.error ?? false;
};

const coerceFetchedAt = (
  draft: UserPreviewCacheRecordDraft,
  previous: UserPreviewCacheRecord | undefined,
  now: () => number
): number => {
  if ('fetchedAt' in draft && typeof draft.fetchedAt === 'number') {
    return draft.fetchedAt;
  }
  return previous?.fetchedAt ?? now();
};

const coerceDetail = (
  draft: UserPreviewCacheRecordDraft,
  previous: UserPreviewCacheRecord | undefined,
  profile: ProfileResponse | undefined,
  preview: UserPreviewVM | undefined
): UserPreviewCacheDetailLevel => {
  const proposed = draft.detail ?? (profile ? 'full' : undefined) ?? previous?.detail ?? 'partial';

  if (profile) {
    return 'full';
  }

  if (preview && !hasMeaningfulPreview(preview)) {
    return previous?.detail ?? 'partial';
  }

  if (previous?.detail === 'full' && proposed === 'partial') {
    return previous.detail;
  }

  return proposed;
};

const deriveProfileId = (
  draft: UserPreviewCacheRecordDraft,
  previous: UserPreviewCacheRecord | undefined,
  inputCacheKey: string
): string | undefined => {
  if (Object.hasOwn(draft, 'profileId')) {
    const explicit = draft.profileId;
    return typeof explicit === 'string' && explicit.length > 0 ? explicit : undefined;
  }

  const previewSource = decodePayload<UserPreviewVM>(draft.preview);
  const profileSource = decodePayload<ProfileResponse>(draft.profile);

  const fromProfile = typeof profileSource?.id === 'string' ? profileSource.id : undefined;
  const fromPreview = typeof previewSource?.id === 'string' ? previewSource.id : undefined;
  const fromPrevious = previous?.profileId;
  return fromProfile ?? fromPreview ?? fromPrevious ?? extractProfileIdFromKey(inputCacheKey);
};

const deriveUsernameKey = (
  draft: UserPreviewCacheRecordDraft,
  previous: UserPreviewCacheRecord | undefined,
  inputCacheKey: string
): string | undefined => {
  const explicitUsername = Object.hasOwn(draft, 'username') ? draft.username : undefined;
  const previewSource = decodePayload<UserPreviewVM>(draft.preview);
  const profileSource = decodePayload<ProfileResponse>(draft.profile);

  const source =
    explicitUsername ??
    profileSource?.username ??
    previewSource?.username ??
    previous?.usernameKey ??
    extractUsernameFromKey(inputCacheKey) ??
    undefined;

  const normalized = normalizeUsernameKey(source);
  if (normalized) {
    return normalized;
  }
  if (previous?.usernameKey) {
    return previous.usernameKey;
  }
  return;
};

const derivePrimaryCacheKey = ({
  draft,
  previous,
  profileId,
  usernameKey,
  inputCacheKey,
}: {
  draft: UserPreviewCacheRecordDraft;
  previous?: UserPreviewCacheRecord;
  profileId?: string;
  usernameKey?: string;
  inputCacheKey: string;
}): string | undefined => {
  if ('primaryCacheKey' in draft && draft.primaryCacheKey) {
    return draft.primaryCacheKey;
  }

  if (profileId) {
    return buildProfileCacheKey(profileId);
  }

  if (previous?.primaryCacheKey) {
    return previous.primaryCacheKey;
  }

  if (usernameKey) {
    return buildUsernameCacheKey(usernameKey);
  }

  if (isProfileCacheKey(inputCacheKey) || isUsernameCacheKey(inputCacheKey)) {
    return inputCacheKey;
  }

  return;
};

export const normalizeDraft = ({
  draft,
  previous,
  cacheKey,
  now,
}: {
  draft: UserPreviewCacheRecordDraft;
  previous: UserPreviewCacheRecord | undefined;
  cacheKey: string;
  now: () => number;
}): UserPreviewCacheRecord | undefined => {
  const preview = coercePreview(draft, previous);
  const profile = coerceProfile(draft, previous);
  const error = coerceError(draft, previous);
  const fetchedAt = coerceFetchedAt(draft, previous, now);
  const detail = coerceDetail(draft, previous, profile, preview);
  const hasPreviewData = hasMeaningfulPreview(preview);
  const hasErrorFlag = Boolean(error);
  if (!(profile || hasPreviewData || hasErrorFlag)) {
    return previous;
  }
  const profileId = deriveProfileId(draft, previous, cacheKey);
  const usernameKey = deriveUsernameKey(draft, previous, cacheKey);
  const primaryCacheKey = derivePrimaryCacheKey({
    draft,
    inputCacheKey: cacheKey,
    ...(profileId ? { profileId } : {}),
    ...(usernameKey ? { usernameKey } : {}),
    ...(previous ? { previous } : {}),
  });

  if (primaryCacheKey === undefined) {
    return;
  }

  const aliases = mergeAliases({
    ...(Array.isArray(draft.aliases) ? { draftAliases: draft.aliases } : {}),
    ...(previous?.aliases ? { previousAliases: previous.aliases } : {}),
    primaryCacheKey,
    ...(profileId ? { profileId } : {}),
    ...(usernameKey ? { usernameKey } : {}),
    cacheKey,
  });

  const nextRecord: UserPreviewCacheRecord = {
    primaryCacheKey,
    error,
    detail,
    fetchedAt,
    aliases,
    ...(profileId === undefined ? {} : { profileId }),
    ...(usernameKey === undefined ? {} : { usernameKey }),
    ...(preview ? { preview } : {}),
    ...(profile ? { profile } : {}),
  };

  return nextRecord;
};

export const isRecordExpired = (record: UserPreviewCacheRecord, now: () => number, ttlMs: number): boolean =>
  now() - record.fetchedAt > ttlMs;
